import React from "react";
import { UserPlus, Video, Sparkles, ShieldAlert, RefreshCw } from "lucide-react";
import api from "../../lib/apiClient";

/**
 * ActivityPulseStrip — last-24h heartbeat at the top of /admin. Four
 * counters, no clicks needed. Render-once-on-mount with a manual
 * refresh button; the data is cheap (4 count_documents) so we don't
 * need polling. Always renders even at zero so the admin can see
 * "yep, nothing happened in the last 24h" instead of guessing.
 *
 * Eric (Jun 17, 2026): "a daily heartbeat without expanding any
 * section." This is it — sits above the accordion, one row tall,
 * compact even on mobile.
 */
const Tile = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    className="flex-1 min-w-[120px] bg-gray-900/60 border border-gray-800 rounded-lg px-3 py-2.5 flex items-center gap-3"
    data-testid={testId}
  >
    <div className={`h-8 w-8 rounded-full bg-${accent}-500/15 border border-${accent}-500/30 flex items-center justify-center shrink-0`}>
      <Icon className={`h-4 w-4 text-${accent}-300`} />
    </div>
    <div className="min-w-0">
      <div className={`text-${accent}-300 text-lg sm:text-xl font-bold tabular-nums leading-none`}>
        {value}
      </div>
      <div className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5 truncate">
        {label}
      </div>
    </div>
  </div>
);

const ActivityPulseStrip = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/activity-pulse");
      setData(res.data);
    } catch (err) {
      // Non-fatal — the strip is informational. Log so a sustained
      // outage shows up in dev tools rather than silently zero-ing.
      // eslint-disable-next-line no-console
      console.warn("activity-pulse fetch failed:", err);
      setData(null);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div
      className="bg-gray-900/30 border border-gray-800 rounded-xl p-3 sm:p-4 mb-3"
      data-testid="admin-activity-pulse"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-gray-400 text-[10px] font-semibold uppercase tracking-[0.15em]">
          Last 24h
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[10px] text-gray-500 hover:text-gray-200 inline-flex items-center gap-1"
          data-testid="admin-activity-pulse-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <Tile
          icon={UserPlus}
          label="New signups"
          value={data?.new_signups ?? "—"}
          accent="emerald"
          testId="pulse-signups"
        />
        <Tile
          icon={Video}
          label="New videos"
          value={data?.new_videos ?? "—"}
          accent="sky"
          testId="pulse-videos"
        />
        <Tile
          icon={Sparkles}
          label="Commissions claimed"
          value={data?.commissions_claimed ?? "—"}
          accent="amber"
          testId="pulse-commissions"
        />
        <Tile
          icon={ShieldAlert}
          label="Abuse alerts open"
          value={data?.abuse_open ?? "—"}
          accent={data?.abuse_open ? "red" : "gray"}
          testId="pulse-abuse"
        />
      </div>
    </div>
  );
};

export default ActivityPulseStrip;
